import { observer } from "mobx-react";
import { useLayoutEffect, useMemo } from "react";
import { DefaultModalLayout } from "../../../../components/modal/layouts/default-modal-layout";
import FontBody1 from "../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../components/fonts/font-props";
import DateRangePickerV2, { DatePickerVariant } from "../../../../components/date-range-picker-v2/date-range-picker-v2";
import { FormField } from "../../../../components/form-field/form-field";
import "./sbr-receipt-modal.scss";
import useValidationGroup from "../../../../hooks/validation-group/validation-group-hook";
import { Input } from "../../../../components/inputs/input/input";
import FontBody2 from "../../../../components/fonts/font-body2/font-body2";
import { BUTTON_VARIANTS } from "../../../../components/inputs/button-variants";
import SbrReceiptStore from "../../stores/sbr-receipt-store";

interface ISbrReceiptModalProps {
    onClose: () => void;
}

const SBR_RECEIPT_DATES_VALIDATION_ID = "sbr-receipt-date-range";

const SbrReceiptModal: React.FC<ISbrReceiptModalProps> = observer(({ onClose }) => {
    /** Initiate new SbrReceipt */
    const newSbrReceipt = useMemo(() => new SbrReceiptStore(), []);

    /** Initiate validation group and add custom validation for dates range */
    const validationGroup = useValidationGroup();
    const hasValidDates = Boolean(newSbrReceipt.dateFrom?.trim() && newSbrReceipt.dateTo?.trim());
    useLayoutEffect(() => {
        validationGroup.update({ 
            id: SBR_RECEIPT_DATES_VALIDATION_ID, 
            isValid: hasValidDates 
        });
        return () => validationGroup.delete(SBR_RECEIPT_DATES_VALIDATION_ID);
    }, [hasValidDates, validationGroup]);
    
    const onCloseClicked = () => {
        if (newSbrReceipt.isGeneratingInvoice) {
            return;
        }
        onClose();
    };

    return (
        <DefaultModalLayout 
            isCloseDisabled={false} 
            onCloseClicked={onCloseClicked}
            buttons={[
                {
                    text: 'Cancel',
                    onClick: onCloseClicked,
                    hasLoader: true,
                    variant: BUTTON_VARIANTS.Tertiary,
                },
                {
                    text: 'Generate receipt',
                    onClick: () => newSbrReceipt.calculateAndGenerateReceipt(onCloseClicked),
                    hasLoader: true,
                    variant: BUTTON_VARIANTS.Primary,
                    isLoading: newSbrReceipt.isGeneratingInvoice,
                    disabled: !validationGroup.isValid(),
                }
            ]}
        >
            <div className="sbr-receipt-modal">
                <FontBody1 variant={[FONT_VARIANT.BOLD, FONT_VARIANT.UNDER_LINE]}>
                    Configure receipt details:
                </FontBody1>
                
                <FormField label="Select receipt’s dates" required>
                    <DateRangePickerV2
                        value={{ from: newSbrReceipt.dateFrom, to: newSbrReceipt.dateTo }}
                        onChange={(value) => {
                            newSbrReceipt.setDateFrom(value?.from);
                            newSbrReceipt.setDateTo(value?.to);
                        }}
                        variant={DatePickerVariant.DATE_RANGE}
                        placeholder="Choose dates"
                        timeZone="UTC"
                    />
                </FormField>
                <FormField label="Tax ID/Company registration number">
                    <Input value={newSbrReceipt.companyTaxId} onChange={newSbrReceipt.setCompanyTaxId} placeholder="Text" />
                </FormField>
                <FormField label={<>
                    Other <FontBody2 color={FONT_COLOR.PRIMARY2}>(Address, additional information) </FontBody2>
                    <FontBody2 color={FONT_COLOR.PRIMARY3_50}>Max notes 150</FontBody2>
                </>}>
                    <Input value={newSbrReceipt.other} onChange={newSbrReceipt.setOther} placeholder="Merchant details" maxlength={150} />
                </FormField>

                <div className="sbr-receipt-modal-note">
                    <FontBody1>This information will be saved in the system</FontBody1>
                </div>
            </div>
        </DefaultModalLayout>
    );
});

export default SbrReceiptModal;